// ============================================================
// attack/victimNotes - the lines that describe ONE VICTIM rather than
// the shot: their protection, their cover, how the attacker's exposures
// change their reactions. Pure; asserted verbatim - never reword a line.
//
// `hasWS` / `COVER_LABELS` come through the engine BARREL for the hook
// tests' mocks (see `shotNotes`).
// ============================================================
import { hasWS, COVER_LABELS } from '../../engine/index';
import type { Target } from '../../types';

function incorporealNote(sp: string[], target: Target): string | null {
  if (!target.incorporeal) return null;
  return hasWS(sp, 'Force') || hasWS(sp, 'WarpWeapon') || hasWS(sp, 'Sanctified')
    ? 'Target is Incorporeal - weapon can damage it'
    : 'Target is Incorporeal - damage passes through!';
}

function coverNote(target: Target, coverBypass: boolean): string | null {
  const coverLevel = target.cover?.level || 'none';
  const coverAP = target.cover?.ap || 0;
  if (coverLevel === 'none' || coverAP <= 0) return null;
  return coverBypass
    ? 'Cover: ' + COVER_LABELS[coverLevel] + ' (AP ' + coverAP + ') - BYPASSED by the template'
    : 'Cover: ' + COVER_LABELS[coverLevel] + ' (+' + coverAP + ' AP to covered locations)';
}

/**
 * Notes that describe the DEFENDER rather than the shot.
 *
 * They used to be built once, from the aimed target, and pushed into the
 * same list as everything else. With five victims that is a lie per victim -
 * the ork's log would say he was in cover because the guardsman was.
 */
export function victimNotes(sp: string[], target: Target, coverBypass: boolean): string[] {
  const sanctified =
    hasWS(sp, 'Sanctified') && (target.daemonicProtection || 0) > 0
      ? 'Sanctified: bypasses Daemonic Protection (' + target.daemonicProtection + ')'
      : null;
  return [sanctified, incorporealNote(sp, target), coverNote(target, coverBypass)].filter(
    (n): n is string => n !== null,
  );
}

/** "dodge +10, parry -5" - the attacker's reaction exposures, as one clause. */
export function reactionModClause(dodgeMod: number, parryMod: number): string {
  const signed = (n: number) => `${n > 0 ? '+' : ''}${n}`;
  return [dodgeMod ? `dodge ${signed(dodgeMod)}` : '', parryMod ? `parry ${signed(parryMod)}` : '']
    .filter(Boolean)
    .join(', ');
}
